import {
  Chart,
  ChartCategoryAxis,
  ChartCategoryAxisItem,
  ChartLegend,
  ChartSeries,
  ChartSeriesItem,
  ChartSeriesItemTooltip,
  ChartTooltip,
} from '@progress/kendo-react-charts';
import { produce } from 'immer';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { formatPrice } from '../../../../../../common/global/helpers';
import Loader from '../../../../../../common/widgets/Loader';
import { config } from '../../../../config';
import { OrderTimelineModel } from '../../../../models/CompanyStatistics';
import { StatisticsFilterModel } from '../../../../models/StatisticsFilterModel';
import { resources } from '../../../../resources/resources';
import { companyStatisticsService } from '../../../../services/companyStatistics';
import { EmptyStateAdminDashboardOrdersTimeline } from '../../../Common/EmptyStates';

interface OrdersTimelineState {
  isLoading: boolean;
  timeline?: OrderTimelineModel[];
}

interface OrdersTimelineProps {
  filters: StatisticsFilterModel;
}

export const OrdersTimeline = (props: Readonly<OrdersTimelineProps>) => {
  const { t: translate } = useTranslation();
  const [state, setState] = useState<OrdersTimelineState>({
    isLoading: true,
  });

  const loadOrdersTimeline = async (controller: AbortController) => {
    const response = await companyStatisticsService.getOrdersTimeline(
      props.filters,
      {
        signal: controller?.signal,
      },
    );

    if (controller.signal.aborted) return;

    if (!response.hasErrors) {
      setState(
        produce(draft => {
          // render less data points than received
          const length = response.data?.length ?? 0;
          const maxGridElements = 10;

          if (length > maxGridElements) {
            const skip = Math.ceil(length / maxGridElements);
            draft.timeline = response.data.filter((_, ix) => ix % skip === 0);
          } else {
            draft.timeline = response.data;
          }
        }),
      );
    }

    setState(
      produce(draft => {
        draft.isLoading = false;
      }),
    );
  };

  useEffect(() => {
    const controller = new AbortController();

    loadOrdersTimeline(controller);

    return () => {
      controller.abort();
    };
  }, [props.filters]);

  const getTotalCount = () => {
    return [
      {
        name: translate(resources.admin.statistics.ordersTimelineTotalCount),
        color: '#7FD0D6',
        data: state.timeline?.map(t => t.count),
      },
    ];
  };

  const getTotalValue = () => {
    return [
      {
        adjustValue: true,
        name: translate(resources.admin.statistics.ordersTimelineTotalValue),
        color: '#FF220D',
        data: state.timeline?.map(t => t.amount / 100),
      },
    ];
  };

  return (
    <div className="panel padding-xl admin-statistics-section">
      <h2 className="statistics-subtitle">
        {translate(resources.admin.statistics.ordersTimeline)}
      </h2>

      <Loader loading={state.isLoading} loader={<Skeleton />}>
        {state.timeline && state.timeline?.length > 0 ? (
          <div className="">
            <Chart>
              <ChartTooltip render={defaultTooltipRender} />
              <ChartCategoryAxis>
                <ChartCategoryAxisItem
                  categories={state.timeline.map(t => t.date)}
                  startAngle={45}
                  baseUnit="days"
                  labels={{
                    content: e => moment(e.value).format('DD MMM'),
                  }}
                  majorGridLines={{ visible: false }}
                />
              </ChartCategoryAxis>
              <ChartSeries>
                {getTotalCount().map((item, idx) => (
                  <ChartSeriesItem
                    key={idx}
                    type="line"
                    color={item.color}
                    data={item.data}
                    name={item.name}
                  ></ChartSeriesItem>
                ))}
                {getTotalValue().map((item, idx) => (
                  <ChartSeriesItem
                    key={idx}
                    type="line"
                    color={item.color}
                    data={item.data}
                    name={item.name}
                    tooltip={{}}
                  >
                    <ChartSeriesItemTooltip
                      render={valueTooltipRender}
                    ></ChartSeriesItemTooltip>
                  </ChartSeriesItem>
                ))}
              </ChartSeries>
              <ChartLegend visible={true} position="bottom" />
            </Chart>
          </div>
        ) : (
          <EmptyStateAdminDashboardOrdersTimeline />
        )}
      </Loader>
    </div>
  );
};

const defaultTooltipRender = (props: any) => {
  return (
    <span>
      <b>{props.point?.value}</b>
    </span>
  );
};

const valueTooltipRender = (props: any) => {
  const value = props.point?.value * 100;
  return (
    <span>
      <b>
        {formatPrice(value)} {config.currency}
      </b>
    </span>
  );
};
